const quickAddDrawerProductCache = new Map();

class QuickAddDrawer extends HTMLElement {
  connectedCallback() {
    this.gallery = this.querySelector("[data-quick-add-drawer-gallery]");
    this.mediaScroll = this.querySelector(".quick-add-drawer-media-scroll");
    this.dots = this.querySelector("[data-quick-add-drawer-dots]");
    this.content = this.querySelector(".quick-add-drawer-content");
    this.dragHandle = this.querySelector(".quick-add-drawer-handle");
    this.productContainer = this.querySelector("[data-quick-add-drawer-product]");

    this.handleShow = (event) => this.show(event.detail?.source);
    this.handleAddToCart = () => this.hide();
    this.handleKeydown = (event) => {
      if (event.key === "Escape" && this.classList.contains("state--active")) {
        this.hide();
      }
    };
    this.handleClick = (event) => {
      if (event.target.closest("[data-quick-add-drawer-close]")) {
        this.hide();
        return;
      }

      const option = event.target.closest("[data-variant-id]");
      if (option && this.productContainer.contains(option) && this.sourceQuickAdd) {
        this.productContainer
          .querySelectorAll(".quickadd-option.is-selected")
          .forEach((selectedOption) => selectedOption.classList.remove("is-selected"));
        option.classList.add("is-selected");

        const originalOption = this.sourceQuickAdd.querySelector(
          `[data-variant-id="${CSS.escape(option.dataset.variantId)}"]`
        );
        if (originalOption?.dataset.cartGift) option.dataset.cartGift = originalOption.dataset.cartGift;
        if (originalOption?.dataset.allowCheckout) option.dataset.allowCheckout = originalOption.dataset.allowCheckout;
        if (originalOption?.dataset.finalSale) option.dataset.finalSale = originalOption.dataset.finalSale;
        this.sourceQuickAdd.add(event, option);
      }
    };
    this.handleGalleryScroll = () => {
      window.cancelAnimationFrame(this.galleryScrollFrame);
      this.galleryScrollFrame = window.requestAnimationFrame(() => this.updateDots());
    };
    this.handleDragStart = (event) => this.startDrag(event);
    this.handleDragMove = (event) => this.drag(event);
    this.handleDragEnd = (event) => this.endDrag(event);

    document.addEventListener("quickadddrawer:show", this.handleShow);
    document.addEventListener("addtocart", this.handleAddToCart);
    document.addEventListener("keydown", this.handleKeydown);
    this.addEventListener("click", this.handleClick);
    this.gallery.addEventListener("scroll", this.handleGalleryScroll, { passive: true });
    this.content.addEventListener("pointerdown", this.handleDragStart);
    window.addEventListener("pointermove", this.handleDragMove);
    window.addEventListener("pointerup", this.handleDragEnd);
    window.addEventListener("pointercancel", this.handleDragEnd);
  }

  disconnectedCallback() {
    document.removeEventListener("quickadddrawer:show", this.handleShow);
    document.removeEventListener("addtocart", this.handleAddToCart);
    document.removeEventListener("keydown", this.handleKeydown);
    this.removeEventListener("click", this.handleClick);
    this.gallery.removeEventListener("scroll", this.handleGalleryScroll);
    this.content.removeEventListener("pointerdown", this.handleDragStart);
    window.removeEventListener("pointermove", this.handleDragMove);
    window.removeEventListener("pointerup", this.handleDragEnd);
    window.removeEventListener("pointercancel", this.handleDragEnd);
    window.clearTimeout(this.hideTimer);
    window.cancelAnimationFrame(this.galleryScrollFrame);
    this.optionValues?.removeEventListener("scroll", this.handleOptionScroll);
  }

  async show(sourceQuickAdd) {
    if (!sourceQuickAdd) return;

    const productCard = sourceQuickAdd.closest("product-card");
    if (!productCard) return;

    window.clearTimeout(this.hideTimer);
    this.sourceQuickAdd = sourceQuickAdd;
    this.trigger = sourceQuickAdd.querySelector(".quickadd-toggle");
    this.populateGallery(productCard, sourceQuickAdd);
    this.resetDrag();

    const requestId = (this.requestId || 0) + 1;
    this.requestId = requestId;

    try {
      await this.populateProduct(sourceQuickAdd.dataset.productUrl);
    } catch (error) {
      console.error("Quick add drawer: product content failed to load", error);
      this.reset();
      return;
    }

    if (requestId !== this.requestId) return;

    this.setAttribute("aria-hidden", "false");
    this.classList.add("state--active");
    this.content.focus({ preventScroll: true });
  }

  hide() {
    if (!this.classList.contains("state--active")) return;

    this.classList.remove("state--active");
    this.setAttribute("aria-hidden", "true");
    this.trigger?.focus();

    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.reset(), 280);
  }

  reset() {
    this.optionValues?.removeEventListener("scroll", this.handleOptionScroll);
    this.gallery.replaceChildren();
    this.dots.replaceChildren();
    this.productContainer.replaceChildren();
    this.sourceQuickAdd = null;
    this.trigger = null;
    this.optionValues = null;
  }

  populateGallery(productCard, sourceQuickAdd) {
    this.gallery.replaceChildren();
    const seenSources = new Set();

    const appendImage = (image) => {
      const source = image.currentSrc || image.src;
      if (!source || seenSources.has(source)) return;

      seenSources.add(source);
      const item = document.createElement("div");
      item.className = "quick-add-drawer-gallery-item";

      const clone = image.cloneNode(true);
      clone.removeAttribute("id");
      clone.loading = "eager";
      clone.decoding = "async";
      item.appendChild(clone);
      this.gallery.appendChild(item);
    };

    productCard
      .querySelectorAll("[data-product-image] img, [data-product-hover] img")
      .forEach(appendImage);

    const thirdImageSrc = sourceQuickAdd.dataset.thirdImageSrc;
    if (thirdImageSrc && !seenSources.has(thirdImageSrc)) {
      const thirdImage = new Image();
      thirdImage.src = thirdImageSrc;
      thirdImage.alt = sourceQuickAdd.dataset.thirdImageAlt || "";
      thirdImage.loading = "eager";
      thirdImage.decoding = "async";
      appendImage(thirdImage);
    }

    this.gallery.scrollLeft = 0;
    this.mediaScroll?.refresh();
    this.buildDots();
  }

  async populateProduct(productUrl) {
    if (!productUrl) throw new Error("Missing product URL");

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set("section_id", "quick-add-drawer");

    let html = quickAddDrawerProductCache.get(url.href);
    if (!html) {
      const response = await fetch(url.href, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      html = await response.text();
      quickAddDrawerProductCache.set(url.href, html);
    }

    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const productContent = documentFragment.querySelector("[data-quick-add-drawer-product]");
    if (!productContent) throw new Error("Missing quick add product content");

    this.productContainer.replaceChildren(...Array.from(productContent.childNodes).map((node) => node.cloneNode(true)));

    this.optionValues = this.productContainer.querySelector(".quick-add-drawer-option-values");
    if (this.optionValues) {
      this.handleOptionScroll = () => this.updateOptionFade();
      this.optionValues.addEventListener("scroll", this.handleOptionScroll, { passive: true });
      window.requestAnimationFrame(() => this.updateOptionFade());
    }
  }

  buildDots() {
    this.dots.replaceChildren();

    this.gallery.querySelectorAll(".quick-add-drawer-gallery-item").forEach((_, index) => {
      const dot = document.createElement("span");
      dot.className = "quick-add-drawer-dot";
      if (index === 0) dot.classList.add("is-selected");
      this.dots.appendChild(dot);
    });
  }

  updateDots() {
    const dots = Array.from(this.dots.children);
    if (!dots.length) return;

    const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
    const progress = maxScroll > 0 ? this.gallery.scrollLeft / maxScroll : 0;
    const activeIndex = Math.round(progress * (dots.length - 1));

    dots.forEach((dot, index) => dot.classList.toggle("is-selected", index === activeIndex));
  }

  updateOptionFade() {
    if (!this.optionValues) return;

    const maxScroll = this.optionValues.scrollWidth - this.optionValues.clientWidth;
    this.optionValues.classList.toggle("state--fade-left", this.optionValues.scrollLeft > 1);
    this.optionValues.classList.toggle("state--fade-right", this.optionValues.scrollLeft < maxScroll - 1);
  }

  startDrag(event) {
    if (!this.classList.contains("state--active")) return;

    const contentTop = this.content.getBoundingClientRect().top;
    if (event.clientY > contentTop + 56) return;

    this.dragStartY = event.clientY;
    this.dragStartX = event.clientX;
    this.dragDelta = 0;
    this.dragDeltaX = 0;
    this.content.setPointerCapture(event.pointerId);
    this.classList.add("state--dragging");
  }

  drag(event) {
    if (!this.classList.contains("state--dragging")) return;

    this.dragDelta = event.clientY - this.dragStartY;
    this.dragDeltaX = event.clientX - this.dragStartX;
    const translatedDistance = this.dragDelta < 0 ? this.dragDelta * 0.2 : this.dragDelta;
    this.style.setProperty("--quick-add-drawer-drag-y", `${translatedDistance}px`);
  }

  endDrag(event) {
    if (!this.classList.contains("state--dragging")) return;

    if (this.content.hasPointerCapture(event.pointerId)) {
      this.content.releasePointerCapture(event.pointerId);
    }

    const shouldDismiss = this.dragDelta > 12 && this.dragDelta > Math.abs(this.dragDeltaX) * 1.2;
    this.resetDrag();

    if (shouldDismiss) this.hide();
  }

  resetDrag() {
    this.classList.remove("state--dragging");
    this.style.removeProperty("--quick-add-drawer-drag-y");
    this.dragStartY = 0;
    this.dragStartX = 0;
    this.dragDelta = 0;
    this.dragDeltaX = 0;
  }
}

if (!customElements.get("quick-add-drawer")) {
  customElements.define("quick-add-drawer", QuickAddDrawer);
}
